import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { DateTime } from 'luxon';
import Popup from 'reactjs-popup';
import styles from './UltraConfirmationModal.module.scss';
import { ReactComponent as CloseIcon } from '../../assets/images/close-navigation.svg';
import EditShipping, { AddressFields } from '../../components/ModalEditShipping/EditShipping';
import EditBilling, { BillingFields } from '../../components/ModalEditBilling/EditBilling';
import Loading from '../../components/Loading';
import { billingInfoQuery, updateBillingInfoMutation } from '../../graphql-operations/BillingAccount.graphql';
import { updateShippingAddressMutation } from '../../graphql-operations/ShippingInfo.graphql';
import {
  previewUpgradeToUltraQuery,
  purchaseUpgradeToUltraMutation,
} from '../../graphql-operations/UpgradeToUltra.graphql';
import { Address, BillingInfo, gqlTypes } from '../../types';
import { logInternalError } from '../../lib/errors';
import { formattedCentsToDollars } from '../../lib/util';
import useNewDogUltraMemberships, { MembershipChoice, MembershipOption } from '../../hooks/useNewDogUltraMemberships';
import useUpgradeTargetDevice from '../../hooks/useUpgradeTargetDevice';

interface IUltraConfirmationModalProps {
  /** Module ID of the existing-dog context the user is shopping from. */
  moduleId: string;
  trigger?: JSX.Element;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialPetChoice?: 'existing' | 'new';
  onAccept?: () => void;
}

type Step = 'pet-select' | 'membership-select' | 'checkout-review';
type PetChoice = 'existing' | 'new';

interface PetOption {
  id: PetChoice;
  name: string;
  body: string;
}

const buildPets = (existingPetName?: string): PetOption[] => [
  {
    id: 'existing',
    name: existingPetName ?? 'My existing dog',
    body: existingPetName
      ? `Add your Ultra to your existing membership for ${existingPetName}, only pay for the hardware.`
      : 'Add your Ultra to your existing membership, only pay for the hardware.',
  },
  {
    id: 'new',
    name: 'A new dog',
    body: 'Purchase Ultra with a new membership to use on another dog.',
  },
];

interface PricingLine {
  title: string;
  subtitle?: string;
  price: string;
}

const titleFor = (step: Step): string => {
  switch (step) {
    case 'pet-select':
      return 'Who are you shopping for?';
    case 'membership-select':
      return 'Choose a membership';
    case 'checkout-review':
      return 'Checkout';
  }
};

const Disclaimer: React.FC<{ subscriptionEndsAt?: string }> = ({ subscriptionEndsAt }) => (
  <div className={styles.disclaimer}>
    {'By confirming order, you accept the '}
    <a className={styles.disclaimerLink} href="https://tryfi.com/tos" target="_blank" rel="noopener noreferrer">
      {'Terms & Conditions'}
    </a>
    {'.'}
    {subscriptionEndsAt && (
      <> {`Your existing membership renews on ${DateTime.fromISO(subscriptionEndsAt).toFormat('M/d/yyyy')}.`}</>
    )}
  </div>
);

interface PetSelectStepProps {
  pets: PetOption[];
  selected: PetChoice | null;
  subscriptionEndsAt?: string;
  onSelect: (pet: PetChoice) => void;
  onNext: () => void;
}

const PetSelectStep: React.FC<PetSelectStepProps> = ({ pets, selected, subscriptionEndsAt, onSelect, onNext }) => (
  <div className={styles.body}>
    <div className={styles.optionList}>
      {pets.map((pet) => (
        <button
          key={pet.id}
          type="button"
          className={`${styles.optionCard} ${selected === pet.id ? styles.optionCardSelected : ''}`}
          onClick={() => onSelect(pet.id)}
          aria-pressed={selected === pet.id}
        >
          <p className={styles.optionTitle}>{pet.name}</p>
          <p className={styles.optionBody}>{pet.body}</p>
        </button>
      ))}
    </div>
    <button type="button" className={styles.confirmOrderButton} onClick={onNext} disabled={!selected}>
      {'Next'}
    </button>
    <Disclaimer subscriptionEndsAt={subscriptionEndsAt} />
  </div>
);

interface MembershipSelectStepProps {
  memberships: MembershipOption[];
  selected: MembershipChoice | null;
  subscriptionEndsAt?: string;
  onSelect: (m: MembershipChoice) => void;
  onNext: () => void;
}

const MembershipSelectStep: React.FC<MembershipSelectStepProps> = ({
  memberships,
  selected,
  subscriptionEndsAt,
  onSelect,
  onNext,
}) => (
  <div className={styles.body}>
    <div className={styles.optionList}>
      {memberships.map((m) => (
        <button
          key={m.id}
          type="button"
          className={`${styles.optionCard} ${selected === m.id ? styles.optionCardSelected : ''}`}
          onClick={() => onSelect(m.id)}
          aria-pressed={selected === m.id}
        >
          <p className={styles.optionTitle}>{m.label}</p>
          <p className={styles.optionPrice}>{m.price}</p>
          <p className={styles.optionPriceSubtitle}>{m.perWeek}</p>
        </button>
      ))}
    </div>
    <button type="button" className={styles.confirmOrderButton} onClick={onNext} disabled={!selected}>
      {'Checkout'}
    </button>
    <Disclaimer subscriptionEndsAt={subscriptionEndsAt} />
  </div>
);

interface ShippingSummaryProps {
  shippingAddress?: Address;
  onEdit: () => void;
}

const ShippingSummary: React.FC<ShippingSummaryProps> = ({ shippingAddress, onEdit }) => {
  if (!shippingAddress) return null;

  return (
    <div className={styles.summarySection}>
      <div className={styles.summarySectionBody}>
        <p className={styles.summarySectionTitle}>{'Shipping'}</p>
        <p className={styles.summarySectionLine}>{shippingAddress.line1}</p>
        {shippingAddress.line2 && <p className={styles.summarySectionLine}>{shippingAddress.line2}</p>}
        <p className={styles.summarySectionLine}>
          {`${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.zip}`}
        </p>
      </div>
      <button type="button" className={styles.editButton} onClick={onEdit}>
        {'Edit'}
      </button>
    </div>
  );
};

interface BillingSummaryProps {
  billingInfo?: BillingInfo;
  onEdit: () => void;
}

const BillingSummary: React.FC<BillingSummaryProps> = ({ billingInfo, onEdit }) => {
  if (!billingInfo || !billingInfo.paymentInfo) return null;
  const { paymentInfo } = billingInfo;

  return (
    <div className={styles.summarySection}>
      <div className={styles.summarySectionBody}>
        <p className={styles.summarySectionTitle}>{'Billing'}</p>
        {paymentInfo.__typename === 'ObfuscatedCardInfo' && (
          <>
            <p className={styles.summarySectionLine}>{`Card ending in ${paymentInfo.lastFour}`}</p>
            {paymentInfo.month && paymentInfo.year && (
              <p className={`${styles.summarySectionLine} ${styles.summarySectionLineSecondary}`}>
                {`Expires ${paymentInfo.month}/${paymentInfo.year}`}
              </p>
            )}
          </>
        )}
        {paymentInfo.__typename === 'PayPalInfo' && <p className={styles.summarySectionLine}>{'PayPal'}</p>}
        {paymentInfo.__typename !== 'ObfuscatedCardInfo' && paymentInfo.__typename !== 'PayPalInfo' && (
          <p className={styles.summarySectionLine}>{'Payment method on file'}</p>
        )}
      </div>
      <button type="button" className={styles.editButton} onClick={onEdit}>
        {'Edit'}
      </button>
    </div>
  );
};

interface CheckoutPricing {
  lines: PricingLine[];
  subtotalInCents: number;
  shippingInCents: number;
  taxInCents: number;
  totalInCents: number;
}

interface CheckoutReviewStepProps {
  pricing?: CheckoutPricing;
  pricingLoading: boolean;
  pricingError: boolean;
  purchaseLoading: boolean;
  purchaseError?: string;
  billingInfo?: BillingInfo;
  shippingAddress?: Address;
  subscriptionEndsAt?: string;
  onEditShipping: () => void;
  onEditBilling: () => void;
  onConfirm: () => void;
}

const CheckoutReviewStep: React.FC<CheckoutReviewStepProps> = ({
  pricing,
  pricingLoading,
  pricingError,
  purchaseLoading,
  purchaseError,
  billingInfo,
  shippingAddress,
  subscriptionEndsAt,
  onEditShipping,
  onEditBilling,
  onConfirm,
}) => {
  const confirmDisabled = !pricing || pricingLoading || pricingError || purchaseLoading;

  return (
    <div className={styles.body}>
      <div className={styles.lineItem}>
        <div className={styles.lineItemThumb}>
          <img src="/product_images/smart-collar-ultra.png" alt="Fi Ultra" />
        </div>
        <div className={styles.lineItemBody}>
          {pricing?.lines.map((line, idx) => (
            <div key={idx} className={styles.lineItemRow}>
              <div className={styles.lineItemText}>
                <p className={styles.lineItemTitle}>{line.title}</p>
                {line.subtitle && <p className={styles.lineItemSubtitle}>{line.subtitle}</p>}
              </div>
              <div className={styles.lineItemPrice}>{line.price}</div>
            </div>
          ))}
        </div>
      </div>

      <ShippingSummary shippingAddress={shippingAddress} onEdit={onEditShipping} />

      <BillingSummary billingInfo={billingInfo} onEdit={onEditBilling} />

      <div className={styles.totalsDivider} />
      <div className={styles.totalsRow}>
        <div>{'Subtotal'}</div>
        <div>{pricing ? formattedCentsToDollars(pricing.subtotalInCents) : '—'}</div>
      </div>
      <div className={styles.totalsRow}>
        <div>{'Shipping'}</div>
        <div>
          {pricing ? (pricing.shippingInCents === 0 ? 'Free' : formattedCentsToDollars(pricing.shippingInCents)) : '—'}
        </div>
      </div>
      <div className={styles.totalsRow}>
        <div>{'Taxes'}</div>
        <div>{pricing ? formattedCentsToDollars(pricing.taxInCents) : '—'}</div>
      </div>
      <div className={`${styles.totalsRow} ${styles.totalsRowBold}`}>
        <div>{'Total'}</div>
        <div>{pricing ? formattedCentsToDollars(pricing.totalInCents) : '—'}</div>
      </div>

      {pricingError && <p className={styles.errorMessage}>{"We couldn't load pricing. Please try again."}</p>}
      {purchaseError && <p className={styles.errorMessage}>{purchaseError}</p>}

      <button type="button" className={styles.confirmOrderButton} onClick={onConfirm} disabled={confirmDisabled}>
        {purchaseLoading ? 'Processing…' : 'Confirm order'}
      </button>
      <Disclaimer subscriptionEndsAt={subscriptionEndsAt} />
    </div>
  );
};

type EditMode = 'shipping' | 'billing' | null;

const useBillingInfoQuery = () => {
  const { data, refetch } = useQuery<gqlTypes.ECOMMERCE_billingInfoFromBillingAccount>(billingInfoQuery);
  const billingInfo = data?.currentUser?.billingAccount?.billingInfo as BillingInfo | undefined;
  const shippingAddress = data?.currentUser?.billingAccount?.address as Address | undefined;
  return { billingInfo, shippingAddress, refetchBillingInfo: refetch };
};

export default function UltraConfirmationModal({
  moduleId,
  trigger,
  open,
  onOpenChange,
  initialPetChoice,
  onAccept = () => {},
}: IUltraConfirmationModalProps) {
  const initialStep: Step = initialPetChoice === 'existing' ? 'checkout-review' : 'pet-select';
  const [step, setStep] = useState<Step>(initialStep);
  const [selectedPet, setSelectedPet] = useState<PetChoice | null>(initialPetChoice ?? null);
  const [selectedMembership, setSelectedMembership] = useState<MembershipChoice | null>(null);
  const [editMode, setEditMode] = useState<EditMode>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | undefined>(undefined);

  const { billingInfo, shippingAddress, refetchBillingInfo } = useBillingInfoQuery();
  const { petName, subscriptionEndsAt } = useUpgradeTargetDevice(moduleId);

  const newDogUltraMemberships = useNewDogUltraMemberships();
  const pets = buildPets(petName);

  const selectedMembershipOption =
    selectedPet === 'new' && selectedMembership
      ? newDogUltraMemberships.find((m) => m.id === selectedMembership)
      : undefined;

  // Build the preview/purchase input from current selections. Returns null until enough is picked.
  // Existing-dog: moduleId only. New-dog: membershipTermMonths only (no moduleId in the GraphQL
  // input, even though moduleId is in the URL — backend resolver branches on moduleId presence).
  const upgradeInput: gqlTypes.UpgradeToUltraInput | null = (() => {
    if (selectedPet === 'existing') return { moduleId };
    if (selectedPet === 'new' && selectedMembershipOption) {
      return { membershipTermMonths: selectedMembershipOption.renewalMonths };
    }
    return null;
  })();

  const {
    data: previewData,
    loading: previewLoading,
    error: previewError,
    refetch: refetchPreview,
  } = useQuery<gqlTypes.ECOMMERCE_previewUpgradeToUltra, gqlTypes.ECOMMERCE_previewUpgradeToUltraVariables>(
    previewUpgradeToUltraQuery,
    {
      skip: !upgradeInput,
      variables: upgradeInput ? { input: upgradeInput } : undefined,
    },
  );

  const [purchaseUpgrade, { loading: purchaseLoading }] = useMutation<
    gqlTypes.ECOMMERCE_purchaseUpgradeToUltra,
    gqlTypes.ECOMMERCE_purchaseUpgradeToUltraVariables
  >(purchaseUpgradeToUltraMutation);

  const checkoutPricing: CheckoutPricing | undefined = (() => {
    if (!previewData?.previewUpgradeToUltra) return undefined;
    const { shippingInCents, taxInCents, totalInCents } = previewData.previewUpgradeToUltra;
    // Derive subtotal from totals rather than using the gross subtotalInCents from the preview.
    // The GraphQL preview schema doesn't surface `appliedDiscountInCents`, so any backend-applied
    // discount (coupon, etc.) only shows up in totalInCents. Subtracting tax + shipping back out of
    // total gives a displayed subtotal that's net-of-discount and keeps the visible math consistent
    // (Subtotal + Shipping + Tax = Total) without exposing the discount as its own line.
    const subtotalInCents = Math.max(0, totalInCents - taxInCents - shippingInCents);

    // Clamp so the hardware line never displays a negative price when a backend discount is large
    // enough to wipe out the device portion. Membership absorbs whatever the device line can't, so
    // hardware + membership still sums to the displayed subtotal.
    const membershipDisplayedInCents = selectedMembershipOption
      ? Math.min(selectedMembershipOption.priceInCents, subtotalInCents)
      : 0;
    const hardwareDisplayedInCents = subtotalInCents - membershipDisplayedInCents;

    const lines: PricingLine[] =
      selectedPet === 'existing'
        ? [{ title: 'Fi Ultra', subtitle: 'Device only', price: formattedCentsToDollars(subtotalInCents) }]
        : selectedMembershipOption
          ? [
              {
                title: 'Fi Ultra',
                price: formattedCentsToDollars(hardwareDisplayedInCents),
              },
              {
                title: selectedMembershipOption.label,
                price: formattedCentsToDollars(membershipDisplayedInCents),
              },
            ]
          : [];

    return { lines, subtotalInCents, shippingInCents, taxInCents, totalInCents };
  })();

  const [updateBillingMutation] = useMutation<gqlTypes.updateBillingInfo, gqlTypes.updateBillingInfoVariables>(
    updateBillingInfoMutation,
  );
  const [updateShippingMutation] = useMutation<gqlTypes.updateShippingAddress, gqlTypes.updateShippingAddressVariables>(
    updateShippingAddressMutation,
  );

  const reset = () => {
    setStep(initialStep);
    setSelectedPet(initialPetChoice ?? null);
    setSelectedMembership(null);
    setEditMode(null);
    setIsUpdating(false);
    setPurchaseError(undefined);
  };

  const handlePetNext = () => {
    if (selectedPet === 'existing') {
      setStep('checkout-review');
    } else if (selectedPet === 'new') {
      setStep('membership-select');
    }
  };

  const handleMembershipNext = () => {
    if (selectedMembership) setStep('checkout-review');
  };

  const handleConfirmPurchase = (close: () => void) => {
    if (purchaseLoading || !upgradeInput) return;
    setPurchaseError(undefined);
    purchaseUpgrade({ variables: { input: upgradeInput } })
      .then((result) => {
        const purchase = result.data?.purchaseUpgradeToUltra;
        if (!purchase) {
          throw new Error('purchaseUpgradeToUltra resolved without a result');
        }
        onAccept();
        reset();
        close();
      })
      .catch((err) => {
        logInternalError(err);
        setPurchaseError("We couldn't complete your purchase. Please try again.");
      });
  };

  const handleUpdateAddress = (addressFields: AddressFields, recaptchaToken: string) => {
    if (isUpdating) return;
    setIsUpdating(true);
    updateShippingMutation({
      variables: {
        firstName: billingInfo?.firstName || '',
        lastName: billingInfo?.lastName || '',
        address: {
          line1: addressFields.line1,
          line2: addressFields.line2 || null,
          city: addressFields.city,
          state: addressFields.state,
          zip: addressFields.zip,
          country: addressFields.country,
        },
        recaptchaToken,
      },
    })
      .then((result) => {
        if (!result.data?.updateBillingAccount?.address) {
          throw new Error('updateShippingAddress resolved without an updated address');
        }
        refetchBillingInfo();
        refetchPreview();
        setEditMode(null);
      })
      .catch((err) => {
        logInternalError(err);
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  const handleUpdateBilling = (billingFields: BillingFields, recaptchaToken: string) => {
    if (isUpdating) return;
    setIsUpdating(true);
    updateBillingMutation({
      variables: {
        input: { billingInfo: { token: billingFields.paymentInfo.token || '' } },
        recaptchaToken,
      },
    })
      .then((result) => {
        if (!result.data?.updateBillingAccount?.billingInfo) {
          throw new Error('updateBillingInfo resolved without updated billing info');
        }
        refetchBillingInfo();
        refetchPreview();
        setEditMode(null);
      })
      .catch((err) => {
        logInternalError(err);
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  return (
    <Popup
      className="s3-popup"
      trigger={trigger}
      open={open}
      modal
      onClose={() => {
        reset();
        onOpenChange?.(false);
      }}
    >
      {(close: () => void) => {
        const closeAndReset = () => {
          reset();
          close();
        };

        if (editMode === 'shipping' && shippingAddress && billingInfo) {
          return (
            <EditShipping
              onUpdateAddress={handleUpdateAddress}
              onCancel={() => setEditMode(null)}
              initialAddress={shippingAddress as AddressFields}
            />
          );
        }
        if (editMode === 'billing' && billingInfo) {
          return (
            <EditBilling
              onUpdateBilling={handleUpdateBilling}
              onCancel={() => setEditMode(null)}
              initialBilling={billingInfo}
              shippingAddress={shippingAddress}
            />
          );
        }
        if (isUpdating) return <Loading />;

        return (
          <div className={styles.container}>
            <div className={styles.header}>
              <div className={styles.title}>{titleFor(step)}</div>
              <button type="button" className={styles.closeIcon} aria-label="Close" onClick={closeAndReset}>
                <CloseIcon />
              </button>
            </div>
            {step === 'pet-select' && (
              <PetSelectStep
                pets={pets}
                selected={selectedPet}
                subscriptionEndsAt={subscriptionEndsAt}
                onSelect={setSelectedPet}
                onNext={handlePetNext}
              />
            )}
            {step === 'membership-select' && (
              <MembershipSelectStep
                memberships={newDogUltraMemberships}
                selected={selectedMembership}
                subscriptionEndsAt={subscriptionEndsAt}
                onSelect={setSelectedMembership}
                onNext={handleMembershipNext}
              />
            )}
            {step === 'checkout-review' && selectedPet && (
              <CheckoutReviewStep
                pricing={checkoutPricing}
                pricingLoading={previewLoading}
                pricingError={!!previewError}
                purchaseLoading={purchaseLoading}
                purchaseError={purchaseError}
                billingInfo={billingInfo}
                shippingAddress={shippingAddress}
                subscriptionEndsAt={subscriptionEndsAt}
                onEditShipping={() => setEditMode('shipping')}
                onEditBilling={() => setEditMode('billing')}
                onConfirm={() => handleConfirmPurchase(close)}
              />
            )}
          </div>
        );
      }}
    </Popup>
  );
}
